import { documentTitle, usePageTitle } from "../lib/routes.js";
import "./auth.css";

/**
 * What the reader does, for anyone who finds it in their logs.
 *
 * Public and unauthenticated on purpose. `CruxBot/1.0` — the User-Agent on
 * every request `site-reader.ts` makes — carries a link to this page, and a
 * login wall answers none of the questions a stranger with our requests in
 * their logs actually has: who are you, what did you read, how do I stop you.
 *
 * Every claim here has to stay true of the code. It describes a reader that
 * takes four pages at most, checks robots before each one, and stops when it is
 * told to. When that stops being how it works, this page becomes a
 * misrepresentation rather than an out-of-date marketing line, and the header
 * that points here becomes a broken promise.
 */

/** Named once, so the page and the crawler's contact line cannot drift apart. */
const CONTACT = "rahul@emiactech.com";

export function AboutScreen() {
  usePageTitle(documentTitle("About"));
  return (
    <div className="page">
      <div className="container narrow">
        <h1 className="page-title">Crux</h1>

        <p className="auth-lede">
          Crux measures whether AI answer engines name your brand. Part of it reads a
          company&rsquo;s own public web pages, and this page is about that reader.
        </p>

        <section className="card about-card">
          <h2>What it reads</h2>
          <p>
            When someone asks Crux about a company, the reader opens that company&rsquo;s
            own website: the homepage and at most three paths that usually say what the
            company sells. Four pages, once, and no crawl &mdash; nothing it reads is
            followed anywhere else.
          </p>
        </section>

        <section className="card about-card">
          <h2>How it identifies itself</h2>
          <p>
            Every request carries <strong>CruxBot/1.0</strong> and a link back to this
            page. A silent reader is rude, and someone whose server we touched should be
            able to find out who we are from the request itself.
          </p>
        </section>

        <section className="card about-card">
          <h2>What it does not do</h2>
          <ul>
            <li>
              It does not crawl. Four pages at most, each one chosen by path, and links
              are never followed.
            </li>
            <li>
              It checks <strong>robots.txt</strong> before every fetch and a refusal ends
              the read. There is no path where a disallowed page is noticed and fetched
              anyway, and the decision is recorded with the read.
            </li>
            <li>
              Every request has a timeout and a size ceiling, enforced while the page is
              being read rather than after it has all arrived.
            </li>
            <li>It has no account anywhere, and it reads public pages only.</li>
          </ul>
        </section>

        <section className="card about-card">
          <h2>Contact</h2>
          <p>
            Questions, or a request to stop: <a href={`mailto:${CONTACT}`}>{CONTACT}</a>.
            Our crawler identifies itself in its User-Agent and links back to this page.
          </p>
        </section>
      </div>
    </div>
  );
}
